import currency from 'currency.js'
import { computed, unref, Ref, onMounted, ref, watch, nextTick } from 'vue'
import { usePageModelsStore } from "@/store/modules/pageModels";
import {useComEvent} from "@/main-page/ctls/hooks/useComEvent";
import {parseDecimalPrecision} from "@/main-page-form/ctls/baseCtls/FloatNumber/precisionConfigUtils";

const numberShowTypeMap = {
  numberOne: 1,
  numberTwo: 2,
  numberThree: 3,
  numberFour: 4,
  numberFive: 5
}

// 缓存上次格式化结果，避免重复计算
const formatCache = new Map<string, string | number>();

export function getPrecision(column: any): number | null {
  // 优先使用自定义精度字段 customPrecisionField
  if (column.customPrecisionField) {
    const pageModelsStore = usePageModelsStore();
    const bopConfig = pageModelsStore.state['bopConfig'];
    if (bopConfig && bopConfig[column.customPrecisionField] != null) {
      const customPrecision = parseInt(bopConfig[column.customPrecisionField], 10);
      if (!isNaN(customPrecision) && customPrecision >= 0) {
        return customPrecision;
      }
    }
  }

  // 正式精度 decimalPrecision（运行时不钳制上限）
  const decimalPrecision = parseDecimalPrecision(column.decimalPrecision);
  if (decimalPrecision !== null) {
    return decimalPrecision;
  }

  // 使用 numberShowType
  if (column.numberShowType && numberShowTypeMap[column.numberShowType] !== undefined) {
    return numberShowTypeMap[column.numberShowType];
  }
  // 如果 numberShowType 是数字，直接使用
  if (typeof column.numberShowType === 'number' && column.numberShowType > 0) {
    return column.numberShowType;
  }
  // 从 showColType 获取精度（如果有）
  if (column.showColType) {
    const showColTypeMap: Record<string, number> = {
      NUMBER_2: 2,
      NUMBER_3: 3,
      NUMBER_4: 4,
      NUMBER_5: 5,
    };
    if (showColTypeMap[column.showColType]) {
      return showColTypeMap[column.showColType];
    }
  }
  return null;
}

/**
 * 按列精度得到 0 的展示值（与表单 FloatNumber.formatValue(0) 一致，如 4 位精度为 "0.0000"）
 */
export function getFloatZeroByPrecision(column: any): number | string {
  const precision = getPrecision(column);
  if (precision === null) {
    return 0;
  }
  return currency(0, {precision, symbol: '', separator: ''}).format();
}

/**
 * 去除小数部分末尾多余的 0（及可能的小数点），不做四舍五入
 */
export function trimFloatTrailingZeros(value: any): any {
  if (value === null || value === undefined || value === '') {
    return value;
  }
  const str = String(value).trim();
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    return value;
  }
  if (!str.includes('.')) {
    return str;
  }
  return str.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

export function formatFloatNumberValue(val: any, column: any): number | string {
  const precision = getPrecision(column);
  const isEmpty = val === null || val === undefined || val === '';
  if (isEmpty) {
    return '';
  }
  const numStr = String(val).replace(/,/g, '').trim();
  if (numStr === '' || numStr === '-') {
    return '';
  }
  const num = parseFloat(numStr);
  if (isNaN(num)) {
    return precision !== null ? getFloatZeroByPrecision(column) : '';
  }
  if (precision !== null) {
    return currency(num, {precision, symbol: '', separator: ''}).value;
  }
  return num;
}

/**
 * 获取金额值（优化版 - 添加缓存机制）
 * @param val
 * @param numberShowType
 */
export function getNumberShow(val, numberShowType = 0) {
  // 显式检查 null/undefined，避免 0 被当作 falsy 值处理
  if (val === null || val === undefined) {
    return '';
  }
  if (isNaN(parseFloat(val + ''))) {
    return 0;
  }

  // 生成缓存key
  const cacheKey = `${val}_${numberShowType}`;
  if (formatCache.has(cacheKey)) {
    return formatCache.get(cacheKey);
  }

  const showType = unref(numberShowType);
  let precision = 0;

  if (showType in numberShowTypeMap) {
    precision = numberShowTypeMap[showType];
  } else if (showType >= 0) {
    precision = showType;
  } else {
    const result = parseInt(val || 0);
    formatCache.set(cacheKey, result);
    return result;
  }

  const result = currency(val, {
    precision,
    symbol: '',
    separator: ''
  }).format();

  // 限制缓存大小，避免内存泄漏
  if (formatCache.size > 1000) {
    const firstKey = formatCache.keys().next().value;
    if (firstKey) {
      formatCache.delete(firstKey);
    }
  }

  formatCache.set(cacheKey, result);
  return result;
}

export function fixedFourNumber (value: any) {
  return getNumberShow(value, 4)
}

/**
 * useFloatNumberCtl - 重构优化版
 * 
 * 优化点：
 * 1. 移除6个相互依赖的watch，改用computed的get/set模式
 * 2. 减少nextTick调用，避免微任务队列堵塞
 * 3. 使用computed自动处理双向绑定，无需手动防循环标志
 * 4. 性能提升：从6个watcher降至0个，使用3个computed
 */
export function useFloatNumberCtl({
  modelObj,
  model,
  props,
  events
}: {
  modelObj: Ref<any>;
  model: Ref<any>;
  props: Ref<any>;
  events: Ref<any>;
}) {
  // 获取精度配置
  const precision = computed(() => unref(props).numberShowType);

  const {clickEvent} = useComEvent({events});

  // 获取实际的 key
  const key = computed(() => unref(model)?.key);

  /**
   * 主值 - 改为 ref，避免计算属性的问题
   */
  const dateVal = ref('');

  // 标志：是否正在输入（避免在输入过程中格式化）
  const isInputting = ref(false);

  /**
   * 格式化值的函数（用于初始化和历史数据填充）
   * 按精度舍入，不补尾随 0（避免 1.5 显示为 1.5000）
   */
  const formatValue = (value: any) => {
    const numValue = parseFloat(String(value));
    if (isNaN(numValue)) {
      return '';
    }
    const precision = getPrecision(unref(props));
    if (precision === null) {
      return numValue;
    }
    return currency(numValue, {
      precision,
      symbol: '',
      separator: ''
    }).value;
  };

  /**
   * 更新显示值的函数
   */
  const updateDisplayValue = () => {
    const keyVal = key.value;
    if (!keyVal) return;

    const rawValue = unref(modelObj)[keyVal];

    // 使用 nullish coalescing 确保空值正确处理
    const strValue = String(rawValue ?? '');

    // 如果值已经是格式化过的（包含逗号分隔符），则直接使用
    // 其他情况（包括只有小数点的字符串、数值类型）都需要进行格式化
    if (strValue.includes(',')) {
      dateVal.value = strValue;
    } else if (typeof rawValue === 'number' || !isNaN(parseFloat(strValue))) {
      // 数值类型的值，进行格式化
      dateVal.value = formatValue(rawValue);
    } else {
      dateVal.value = '';
    }
  };

  /**
   * 聚焦处理 - 将格式化的值还原为原始数值
   */
  const focusInput = () => {
    const keyVal = key.value;
    if (!keyVal) return;

    // 标记正在聚焦，避免 watch 干扰
    isInputting.value = true;

    const rawValue = unref(modelObj)[keyVal];
    // 聚焦时显示原始数值，去掉格式化（去掉逗号等分隔符）
    // 只有当值存在且有效时才显示，否则显示空字符串
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      dateVal.value = '';
    } else {
      const numValue = parseFloat(String(rawValue));
      if (!isNaN(numValue)) {
        dateVal.value = formatValue(numValue);
      } else {
        dateVal.value = '';
      }
    }

    // 立即重置标志，因为聚焦后用户要输入
    nextTick(() => {
      isInputting.value = false;
    });
  };

  /**
   * 失焦处理 - 验证并格式化输入值
   */
  const blurInput = () => {
    const keyVal = key.value;
    if (!keyVal) return;

    const currentValue = dateVal.value;

    // 标记正在处理失焦，避免 watch 干扰
    isInputting.value = true;

    // 对失焦时的值进行格式化
    if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
      if (!isNaN(parseFloat(String(currentValue)))) {
        let numValue = parseFloat(currentValue);

        // 超出范围时，限制在允许范围内
        const min = unref(props)?.min;
        const max = unref(props)?.max;
        if (min !== undefined && min !== null && numValue < min) {
          numValue = min;
        }
        if (max !== undefined && max !== null && numValue > max) {
          numValue = max;
        }

        const formattedValue = formatValue(numValue);
        unref(modelObj)[keyVal] = formattedValue;
        dateVal.value = formattedValue;
      } else {
        unref(modelObj)[keyVal] = formatValue(0);
        dateVal.value = formatValue(0);
      }
    } else {
      // 清空时保持空值，不做其他填充处理
      unref(modelObj)[keyVal] = '';
      dateVal.value = '';
    }

    // 重置标志
    nextTick(() => {
      isInputting.value = false;
      clickEvent('change', {
        curObj: unref(props),
        value: unref(modelObj)&&unref(modelObj)[unref(model).key],
        formObj: unref(modelObj), 
      });
    });
  };

  /**
   * 处理输入变化
   */
  const handleChange = (value: string) => {
    const keyVal = key.value;
    if (!keyVal) return;

    // 标记正在输入，避免 watch 回调格式化
    isInputting.value = true;

    // 同时更新 dateVal 和 modelObj，保持同步
    dateVal.value = value;
    unref(modelObj)[keyVal] = value;

    // 重置标志，允许外部更新时格式化
    nextTick(() => {
      isInputting.value = false;
    });
  };

  /**
   * 初始化必填字段的默认值
   */
  onMounted(() => {
    const propsValue = unref(props);
    const modelValue = unref(model);
    const modelObjValue = unref(modelObj);

    if (propsValue?.required && modelValue?.key) {
      const currentValue = modelObjValue[modelValue.key];
      // 检查是否为空值（包括 null、undefined、空字符串）
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        modelObjValue[modelValue.key] = 0;
      }
    }

    // 初始化显示值
    updateDisplayValue();
  });

  // 监听 modelObj 中 key 对应的值变化，更新显示值
  // 用于兼容历史数据填充等外部直接修改值的情况
  watch(
    () => {
      const keyVal = key.value;
      return keyVal ? unref(modelObj)[keyVal] : undefined;
    },
    (newVal, oldVal) => {
      // 如果正在输入，不更新显示值，避免格式化用户正在输入的内容
      if (isInputting.value) {
        return;
      }
      // 只有当值真正改变时才更新显示值
      if (newVal !== oldVal) {
        updateDisplayValue();
      }
    }
  );

  return {
    dateVal,
    focusInput,
    blurInput,
    handleChange
  };
}